import { useMutation, useQuery } from '@tanstack/react-query';
import type { GestifyCheckResponse, GestifyStatusResponse } from '@k-management/shared';
import { useState } from 'react';
import { GestifyConnectDialog } from '../../components/GestifyConnectDialog.js';
import { apiRequest } from '../../lib/api.js';
import { useGestifyBrowser } from '../../lib/gestify-browser.js';

/**
 * Administrators: the connection of this environment to Gestify, read only
 * (docs/adr/ADR-011). It shows the settings (never the password) and checks
 * the path up to reading subjects and sales documents: only counts come back.
 */
export function GestifyPage() {
  const status = useQuery({
    queryKey: ['gestify'],
    queryFn: () => apiRequest<GestifyStatusResponse>('/gestify'),
  });
  const check = useMutation({
    mutationFn: () => apiRequest<GestifyCheckResponse>('/gestify/check', { method: 'POST' }),
  });
  const data = status.data;

  return (
    <section className="stack">
      <div className="page-header">
        <div className="stack tight">
          <h1>Gestify</h1>
          <p className="muted">
            Collegamento di questo ambiente a Gestify, in sola lettura (ADR-011). La verifica entra
            con l’utente dell’integrazione e conta anagrafiche e documenti: non scrive nulla.
          </p>
        </div>
      </div>
      {status.isPending && <p className="muted">Caricamento…</p>}
      {status.isError && <p className="error">{status.error.message}</p>}
      <BrowserConnection />
      {data && (
        <>
          <section className="card stack">
            <h2>Impostazioni dell’ambiente</h2>
            {!data.configured && (
              <p className="notice">
                Questo ambiente non ha ancora l’utente di Gestify: ambiente, utente e password si
                aggiungono in Secret Manager con una PR in K-Apps-Platform.
              </p>
            )}
            <dl className="facts">
              <Fact
                label="Ambiente Gestify"
                value={
                  data.environment === 'beta'
                    ? 'Beta (prove)'
                    : data.environment === 'production'
                      ? 'Produzione'
                      : null
                }
              />
              <Fact label="Servizio di accesso" value={data.hosts?.auth ?? null} />
              <Fact label="Servizio dati" value={data.hosts?.data ?? null} />
              <Fact label="Utente dell’integrazione" value={data.username} />
              <Fact
                label="Azienda"
                value={data.companyId === null ? null : String(data.companyId)}
                empty="quella dell’utente"
              />
            </dl>
          </section>

          <section className="card stack">
            <div className="row between">
              <h2>Verifica dal server</h2>
              <button
                type="button"
                className="primary"
                disabled={check.isPending}
                onClick={() => check.mutate()}
              >
                {check.isPending ? 'Verifica in corso…' : 'Verifica collegamento'}
              </button>
            </div>
            {check.isError && <p className="error">{check.error.message}</p>}
            {check.data && (
              <>
                <p className={check.data.ok ? 'notice info' : 'notice'}>
                  {check.data.ok
                    ? 'Collegamento riuscito: il server entra in Gestify e ne legge i dati.'
                    : 'Il server non entra in Gestify: il primo passaggio non riuscito dice cosa manca. Il firewall di Gestify oggi blocca le chiamate da Google Cloud: si lavora con il collegamento dal browser.'}
                </p>
                <ol className="check-steps">
                  {check.data.steps.map((step) => (
                    <li key={step.key} className={step.ok ? 'ok' : 'failed'}>
                      <span className="check-mark" aria-hidden="true">
                        {step.ok ? '✓' : '✕'}
                      </span>
                      <span>
                        <strong>{step.label}</strong>
                        <small className="muted block">{step.detail}</small>
                      </span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
        </>
      )}
    </section>
  );
}

/**
 * The connection of this browser to Gestify (ADR-017): the way that works
 * while Gestify's firewall refuses our server. Only counts are read here.
 */
function BrowserConnection() {
  const gestify = useGestifyBrowser();
  const [connecting, setConnecting] = useState(false);
  const check = useMutation({
    mutationFn: async () => ({
      subjects: await gestify.count('/api/anagrafica/v1/list'),
      invoices: await gestify.count('/api/documenti/documenti_fiscali_vendita/v1/list'),
    }),
  });
  return (
    <section className="card stack">
      <div className="row between">
        <h2>Collegamento dal browser</h2>
        {gestify.connectedAs ? (
          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={check.isPending}
              onClick={() => check.mutate()}
            >
              {check.isPending ? 'Verifica in corso…' : 'Verifica dal browser'}
            </button>
            <button type="button" onClick={() => gestify.disconnect()}>
              Scollega
            </button>
          </div>
        ) : (
          <button type="button" className="primary" onClick={() => setConnecting(true)}>
            Collegati a Gestify
          </button>
        )}
      </div>
      <p className="muted">
        Il browser entra in Gestify con il tuo utente, dalla rete dell’ufficio o di casa, e passa i
        dati a K-Management. Serve per importare le anagrafiche, in Anagrafica › Importa.
      </p>
      {gestify.connectedAs && (
        <p className="notice info">Collegato a Gestify come {gestify.connectedAs}.</p>
      )}
      {check.isError && <p className="error">{check.error.message}</p>}
      {check.data && (
        <p className="notice info">
          Gestify risponde: {check.data.subjects ?? '?'} anagrafiche e {check.data.invoices ?? '?'}{' '}
          documenti di vendita.
        </p>
      )}
      {connecting && <GestifyConnectDialog onClose={() => setConnecting(false)} />}
    </section>
  );
}

function Fact({ label, value, empty }: { label: string; value: string | null; empty?: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {value ? (
          <code className="code">{value}</code>
        ) : (
          <span className="muted">{empty ?? '—'}</span>
        )}
      </dd>
    </div>
  );
}
